import { Search as Lunr } from '@/lib/lunr';
import { type User } from '@/db/types';
import { useLocation } from 'wouter';
import {
  useCallback,
  useState,
  useMemo,
  type ChangeEvent,
  type MouseEvent,
  type KeyboardEvent,
} from 'react';

export const useSearch = (users: User[]) => {
  const [_location, navigate] = useLocation();
  const [value, setValue] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const [results, setResults] = useState<{ id: string, name: string }[]>([])

  const index = useMemo(() => {
    const index = Lunr('name');
    index.addIndex('name');
    index.addDocuments([...users]);
    return index;
  }, [users]);

  const onValueChange = useCallback((value: string) => {
    if (!value.length) {
      setValue('');
      setActiveIndex(-1);
      setResults([]);
      return;
    }

    setValue(value);
    setActiveIndex(0);
    setResults(index.search(value));
  }, [index]);
  
  const onChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    onValueChange(e.target.value.trim());
  }, [onValueChange]);

  const onClear = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    onValueChange('');
  }, [onValueChange]);

  const onSelect = useCallback(async (entry: { id: string, name: string }) => {
    if (!entry) return;
    navigate(`/${entry.name}`);
  }, []);

  const onKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      setActiveIndex(activeIndex + 1 < results.length ? activeIndex + 1 : 0);
    }
    if (e.key === 'ArrowUp') {
      setActiveIndex(activeIndex - 1 < 0 ? results.length - 1 : activeIndex - 1);
    }
    if (e.key === 'Enter') {
      onSelect(results[activeIndex]);
    }
  }, [activeIndex, results, onSelect]);

  return {
    activeIndex,
    value,
    results,
    onChange,
    onClear,
    onSelect,
    onKeyDown,
  };
};